import React, { useState, useCallback, useMemo } from "react";
import { RotateCcw, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import GameOptions from "./GameOptions"; // Assumes same directory
import { useGameBot } from "./useGameBot"; // Assumes same directory
import PlayerNameInput from "./PlayerNameInput";

// Grundy numbers (Nim-values) for a wall of size n (Kayles game variant)
// Precomputed for n=0 to ~30. Pattern is periodic eventually but simple lookup is easier.
// Kayles moves: remove 1 or 2 adjacent items (exactly what this game is).
// Sequence A000984 in OEIS? No, Kayles is A000120.
// Let's compute a few on the fly or just use a small lookup.
// G(0)=0
// G(1): moves -> 0. mex(0) = 1.
// G(2): moves -> 0 (remove 2), 1 (remove 1 -> 1+0). mex(0, G(1)=1) = 2.
// G(3): remove 1 -> 2, 1+1. remove 2 -> 1.
// G(2)=2, G(1)^G(1)=0, G(1)=1. Vals: {2, 0, 1} -> mex = 3.
const MEMO_G = {};
const getGrundy = (n) => {
  if (n < 0) return 0;
  if (n === 0) return 0;
  if (MEMO_G[n] !== undefined) return MEMO_G[n];

  const seen = new Set();
  
  // Possible moves:
  // 1. Remove 1 brick at index i (0 to n-1)
  // Splits into i and n-1-i
  for (let i = 0; i < n; i++) {
    seen.add(getGrundy(i) ^ getGrundy(n - 1 - i));
  }

  // 2. Remove 2 bricks at index i (0 to n-2)
  // Splits into i and n-2-i
  for (let i = 0; i < n - 1; i++) {
    seen.add(getGrundy(i) ^ getGrundy(n - 2 - i));
  }

  // Find mex
  let mex = 0;
  while (seen.has(mex)) mex++;
  
  MEMO_G[n] = mex;
  return mex;
};

const BrickWallGame = ({ onBack, setCurrentPage }) => {
  const [walls, setWalls] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [player1Name, setPlayer1Name] = useState("Player 1");
  const [player2Name, setPlayer2Name] = useState("Player 2");
  const [wallInput, setWallInput] = useState("6,2,1");
  const [selectedBricks, setSelectedBricks] = useState([]);

  // New State for Bot
  const [gameMode, setGameMode] = useState("PvP");
  const [difficulty, setDifficulty] = useState("Easy");

  // Initialize walls from input
  const startGame = () => {
    // Validate names handled by user input, but let's just make sure they aren't empty?
    // Actually, controlled inputs so defaults "Player 1/2" are fine if user didn't touch
    // BUT user could clear them.
    if (!player1Name.trim()) setPlayer1Name("Player 1");
    if (gameMode === "PvP" && !player2Name.trim()) setPlayer2Name("Player 2");

    try {
      const wallSizes = wallInput
        .split(",")
        .map((s) => parseInt(s.trim()))
        .filter((n) => n > 0);
      if (wallSizes.length === 0) {
        alert("Please enter valid wall sizes (e.g., 6,2,1)");
        return;
      }

      const initialWalls = wallSizes.map((size, wallIndex) => ({
        id: wallIndex,
        bricks: Array(size)
          .fill(true)
          .map((_, i) => ({ id: i, present: true })),
      }));

      setWalls(initialWalls);
      setGameStarted(true);
      setCurrentPlayer(1);
      setGameOver(false);
      setWinner(null);
      setSelectedBricks([]);
    } catch (error) {
      alert(
        "Invalid input format. Please use numbers separated by commas (e.g., 6,2,1)"
      );
    }
  };

  // Check if game is over (no bricks left)
  const checkGameOver = (currentWalls) => {
    return currentWalls.every((wall) =>
      wall.bricks.every((brick) => !brick.present)
    );
  };

  // Helper to get contiguous segments of bricks from walls
  const getSegments = useCallback((currentWalls) => {
    const segments = [];
    currentWalls.forEach(wall => {
      let currentLen = 0;
      let startIdx = 0;
      wall.bricks.forEach((brick, idx) => {
        if (brick.present) {
          if (currentLen === 0) startIdx = idx;
          currentLen++;
        } else {
          if (currentLen > 0) {
            segments.push({ wallId: wall.id, start: startIdx, length: currentLen });
            currentLen = 0;
          }
        }
      });
      if (currentLen > 0) {
        segments.push({ wallId: wall.id, start: startIdx, length: currentLen });
      }
    });
    return segments;
  }, []);

  // Handle brick selection
  const handleBrickClick = (wallId, brickId) => {
    if (gameOver) return;
    if (gameMode === "PvC" && currentPlayer === 2) return; // Block click during bot turn

    const brickKey = `${wallId}-${brickId}`;
    const wall = walls.find((w) => w.id === wallId);
    const brick = wall.bricks.find((b) => b.id === brickId);

    if (!brick.present) return; // Can't select removed brick

    if (selectedBricks.includes(brickKey)) {
      // Deselect brick
      setSelectedBricks(selectedBricks.filter((key) => key !== brickKey));
    } else {
      // Check selection rules
      if (selectedBricks.length === 0) {
        // First selection
        setSelectedBricks([brickKey]);
      } else if (selectedBricks.length === 1) {
        // Second selection - must be adjacent to first
        const firstBrick = selectedBricks[0];
        const [firstWallId, firstBrickId] = firstBrick.split("-").map(Number);

        if (wallId !== firstWallId) {
          alert("Both bricks must be from the same wall!");
          return;
        }

        if (Math.abs(brickId - firstBrickId) !== 1) {
          alert("Bricks must be adjacent!");
          return;
        }

        setSelectedBricks([...selectedBricks, brickKey]);
      } else {
        // Too many selected
        // Replace selection with new one for better UX? Or just alert.
        // Let's just alert
        alert("You can only select 1 or 2 bricks at a time!");
        return;
      }
    }
  };

  // Execute move (extracted for Bot use)
  const performMove = useCallback((bricksToRemove) => {
     setWalls((currentWalls) => {
        const newWalls = currentWalls.map((wall) => ({
        ...wall,
        bricks: wall.bricks.map((brick) => {
            const brickKey = `${wall.id}-${brick.id}`;
            if (bricksToRemove.includes(brickKey)) {
            return { ...brick, present: false };
            }
            return brick;
        }),
        }));

        // Split walls where gaps are created
        const finalWalls = [];
        let nextWallId = Math.max(...currentWalls.map((w) => w.id)) + 1; // ID doesn't matter much for logic but good for keys

        newWalls.forEach((wall) => {
            // We actually don't NEED to re-id walls for the logic to work, 
            // staying consistent with original ID is better for React keys.
            // But the original code was creating new walls. 
            // Let's stick to simple "present" flag toggling for visual stability if possible.
            // BUT the original code logic "Split walls where gaps are created" was re-creating the wall array.
            // Re-creating walls is safer to match original behavior.
            
           // Original logic adapted:
           // Actually, keeping the same wall ID but just marking 'present: false' is enough for logic.
           // The "Split" logic in original code was complex and might be unnecessary if we just track segments.
           // However, let's keep the original "visual" splitting if desired, but 
           // for Simplicity in this Bot refactor, I will just update the 'present' status of bricks within the SAME wall objects.
           // The UI handles rendering holes fine.
           // Let's discard the "Split walls" logic if it just re-renders; visual holes are fine.
           // WAIT - the original code did: `setWalls(finalWalls)`.
           // Let's stick to updating `present` flag. It's much cleaner.
           return wall; 
        });
        
        // Actually, let's use the simplest update:
        // Updating state directly with newWalls (where Present is updated)
        
        // Re-check Game Over
        if (checkGameOver(newWalls)) {
            setGameOver(true);
            setWinner(currentPlayer); 
        } else {
             setCurrentPlayer(p => p === 1 ? 2 : 1);
        }

        return newWalls;
     });
     
     setSelectedBricks([]);

  }, [currentPlayer]); // checkGameOver is distinct but logic is embedded

  // Wraps performMove for the "Remove" button
  const executeUserMove = () => {
    if (selectedBricks.length === 0) {
      alert("Please select at least one brick to remove!");
      return;
    }
    // We need to pass the current player so update happens correctly? 
    // performMove uses closure state? No, we used functional update for setWalls.
    // But checkGameOver needs the NEW walls.
    // Let's rewrite performMove to be cleaner.
    
    // Actually, let's just copy the logic to `makeMove` and have `executeUserMove` call it.
    makeMove(selectedBricks);
  };


  const makeMove = useCallback(
    (bricksToRemove) => {
      setWalls((prevWalls) => {
        const newWalls = prevWalls.map((wall) => ({
          ...wall,
          bricks: wall.bricks.map((brick) => {
            const brickKey = `${wall.id}-${brick.id}`;
            if (bricksToRemove.includes(brickKey)) {
              return { ...brick, present: false };
            }
            return brick;
          }),
        }));

        if (checkGameOver(newWalls)) {
          setGameOver(true);
          setWinner(currentPlayer === 1 ? 1 : 2); // Current player made the winning move
        } else {
          setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
        }
        return newWalls;
      });
      setSelectedBricks([]);
    },
    [currentPlayer]
  );


  // Bot Strategy
  const getBotMove = useCallback(
    (diff, currentGameState) => {
      const { walls } = currentGameState;
      
      const segments = [];
      walls.forEach(wall => {
        let currentLen = 0;
        let startIdx = 0;
        wall.bricks.forEach((brick, idx) => {
            if (brick.present) {
                if (currentLen === 0) startIdx = idx;
                currentLen++;
            } else {
                if (currentLen > 0) {
                    segments.push({ wallId: wall.id, start: startIdx, length: currentLen });
                    currentLen = 0;
                }
            }
        });
        if (currentLen > 0) {
            segments.push({ wallId: wall.id, start: startIdx, length: currentLen });
        }
      });

      if (segments.length === 0) return null;

      const validMoves = [];
      // Generate all moves
      segments.forEach(seg => {
         // remove 1
         for (let i = 0; i < seg.length; i++) {
             validMoves.push([`${seg.wallId}-${seg.start + i}`]);
         }
         // remove 2
         for (let i = 0; i < seg.length - 1; i++) {
             validMoves.push([`${seg.wallId}-${seg.start + i}`, `${seg.wallId}-${seg.start + i + 1}`]);
         }
      });
      
      if (diff === "Easy") {
          const randomIndex = Math.floor(Math.random() * validMoves.length);
          return validMoves[randomIndex];
      }
      
       if (diff === "Medium") {
          // Heuristic: Avoid leaving small even piles? Or just Random. 
          // Let's use Random for Medium too as simpler heuristic is hard to define without full Nim sum.
          const randomIndex = Math.floor(Math.random() * validMoves.length);
          return validMoves[randomIndex];
      }

      if (diff === "Hard") {
          // Nim-Sum Strategy
          // Calculate current Nim-sum
          let nimSum = 0;
          segments.forEach(seg => {
              nimSum ^= getGrundy(seg.length);
          });
          
          if (nimSum === 0) {
              // Losing position, play random
               const randomIndex = Math.floor(Math.random() * validMoves.length);
               return validMoves[randomIndex];
          }
          
          // Find a move that makes newNimSum == 0
          for (let move of validMoves) {
              // We need to see what segments this move modifies
              // This is a bit tricky to reverse engineer quickly from just the move array.
              // Easier: Just look at the segment `seg` this move came from.
              // A move in `seg` splits it into `left` and `right` (possibly 0 length).
              // New State NimSum = (CurrentNimSum ^ G(seg.length)) ^ G(left) ^ G(right).
              // We want this to be 0.
              
              // Let's re-iterate segments and generate moves within loops to have context
          }
          
          // Optimized search
           for (let seg of segments) {
                const currentG = getGrundy(seg.length);
                const targetG = nimSum ^ currentG; // We want new G to be this
                
                // Can we reach targetG from seg?
                if (targetG < currentG) {
                    // There exists a move to reduce G(seg) to targetG?
                    // We need to check splits.
                    // Remove 1 at i: G(i) ^ G(len-1-i)
                    for (let i = 0; i < seg.length; i++) {
                        if ((getGrundy(i) ^ getGrundy(seg.length - 1 - i)) === targetG) {
                             return [`${seg.wallId}-${seg.start + i}`];
                        }
                    }
                    // Remove 2 at i: G(i) ^ G(len-2-i)
                     for (let i = 0; i < seg.length - 1; i++) {
                        if ((getGrundy(i) ^ getGrundy(seg.length - 2 - i)) === targetG) {
                             return [`${seg.wallId}-${seg.start + i}`, `${seg.wallId}-${seg.start + i + 1}`];
                        }
                    }
                }
           }
           
           // If we couldn't find winning move (shouldn't happen if nimSum!=0), random
           return validMoves[0];
      }

      return validMoves[0];
    },
    []
  );

  // Hook for Bot
  const { isBotThinking } = useGameBot({
    gameMode,
    makeMove,
    playerTurn: currentPlayer,
    gameStarted,
    gameOver,
    botStrategy: getBotMove,
    gameState: { walls },
    difficulty,
  });


  // Helper for UI
  const getCurrentPlayerName = () => {
      if (gameMode === "PvC" && currentPlayer === 2) return "Computer";
      return currentPlayer === 1 ? player1Name : player2Name;
  };

  const getWinnerName = () => {
    if (gameMode === "PvC" && winner === 2) return "Computer";
    return winner === 1 ? player1Name : player2Name;
  };

  const totalBricks = walls.reduce(
    (total, wall) =>
      total + wall.bricks.filter((brick) => brick.present).length,
    0
  );

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <Link
          to="/games"
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          ← Back to Games
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">
          Brick Wall Game
        </h1>
        <p className="text-center text-gray-600 mb-8">
          Remove 1 or 2 adjacent bricks from any wall - last player to move
          wins!
        </p>

        {!gameStarted ? (
          <div className="text-center">
            <GameOptions
              gameMode={gameMode}
              setGameMode={setGameMode}
              difficulty={difficulty}
              setDifficulty={setDifficulty}
              onStart={startGame}
            />

            <PlayerNameInput
              player1Name={player1Name}
              setPlayer1Name={setPlayer1Name}
              player2Name={player2Name}
              setPlayer2Name={setPlayer2Name}
              gameMode={gameMode}
            />

            <div className="mb-6 max-w-lg mx-auto">
              <label className="block text-lg font-medium text-gray-700 mb-2">
                Wall Sizes (comma separated):
              </label>
              <input
                type="text"
                value={wallInput}
                onChange={(e) => setWallInput(e.target.value)}
                placeholder="e.g., 6,2,1"
                className="px-4 py-2 text-lg border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
              <p className="text-sm text-gray-500 mt-1">
                Example: "6,2,1" creates walls with 6, 2, and 1 bricks
              </p>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div className="text-xl font-semibold">
                {gameOver ? (
                  <span className="text-green-600 flex items-center">
                    <Trophy className="h-6 w-6 mr-2" />
                    {getWinnerName()} Wins!
                  </span>
                ) : (
                  <span>
                    Current Player:
                    <span
                      className={
                        currentPlayer === 1
                          ? "text-blue-600 ml-2"
                          : "text-red-600 ml-2"
                      }
                    >
                      {getCurrentPlayerName()}
                    </span>
                    {isBotThinking && <span className="ml-2 text-sm text-gray-500">(Thinking...)</span>}
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-600">
                Bricks remaining: {totalBricks}
              </div>
              <button
                onClick={() => {
                   setGameStarted(false);
                   setWalls([]);
                   setSelectedBricks([]);
                   setCurrentPlayer(1);
                   setGameOver(false);
                }}
                className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                New Game
              </button>
            </div>

            {/* Walls display */}
            <div className="space-y-6 mb-6">
              {walls.map((wall) => (
                <div key={wall.id} className="text-center">
                  <div className="text-sm text-gray-500 mb-2">
                    Wall {wall.id + 1} (
                    {wall.bricks.filter((b) => b.present).length} bricks)
                  </div>
                  <div className="flex justify-center items-end">
                    {wall.bricks.map((brick) => {
                      const brickKey = `${wall.id}-${brick.id}`;
                      const isSelected = selectedBricks.includes(brickKey);

                      return (
                        <button
                          key={brick.id}
                          onClick={() => handleBrickClick(wall.id, brick.id)}
                          disabled={gameOver || !brick.present || isBotThinking}
                          className={`
                            w-12 h-8 m-px border-2 transition-all duration-200
                            ${
                              brick.present
                                ? isSelected
                                  ? "bg-red-400 border-red-600 transform -translate-y-1 shadow-lg"
                                  : "bg-red-600 border-red-800 hover:bg-red-500 cursor-pointer"
                                : "bg-transparent border-transparent"
                            }
                            ${!gameOver && brick.present && !isBotThinking && "hover:shadow-md"}
                          `}
                          title={
                            brick.present
                              ? `Click to ${
                                  isSelected ? "deselect" : "select"
                                } brick`
                              : "Removed"
                          }
                        >
                          {brick.present && "🧱"}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Game controls */}
            {!gameOver && (
              <div className="text-center space-y-4">
                <div className="text-lg">
                  Selected: {selectedBricks.length} brick
                  {selectedBricks.length !== 1 ? "s" : ""}
                </div>
                <div className="space-x-4">
                  <button
                    onClick={executeUserMove}
                    disabled={selectedBricks.length === 0 || isBotThinking}
                    className="px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Remove Selected Bricks
                  </button>
                  <button
                    onClick={() => setSelectedBricks([])}
                    disabled={selectedBricks.length === 0 || isBotThinking}
                    className="px-6 py-3 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Clear Selection
                  </button>
                </div>
                {!isBotThinking && (
                <p className="text-sm text-gray-600">
                  Click bricks to select them (max 2 adjacent bricks from same
                  wall), then remove them!
                </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrickWallGame;

